import { observer } from 'mobx-react-lite'
import { firstError } from '@/features/legends/editor/validation'
import type { QuestionEditorViewModelBase } from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { QuestionsBuilderViewModel } from '@/features/legends/questions/QuestionsBuilderViewModel'
import { QuestionCard } from '@/features/legends/questions/components/QuestionCard'
import {
  QuestionList,
  QuestionListEmptyState,
  QuestionListPathEmptyState,
} from '@/features/legends/questions/components/QuestionList'
import { QuestionPreviewContainer } from '@/features/legends/questions/components/QuestionPreview/QuestionPreviewContainer'
import { asAnyQuestionEditor } from '@/features/legends/questions/question-registry'

export interface QuestionListContainerProps {
  vm: QuestionsBuilderViewModel
  testId?: string
}

/**
 * A builder's questions as cards. Paths show their own list inside.
 */
export const QuestionListContainer = observer(function QuestionListContainer({
  vm,
  testId,
}: QuestionListContainerProps) {
  return (
    <QuestionList
      isEmpty={vm.isEmpty}
      empty={
        vm.parentQuestion ? (
          <QuestionListPathEmptyState />
        ) : (
          <QuestionListEmptyState />
        )
      }
      canAcceptDrop={vm.canAcceptDrop}
      onDrop={vm.acceptDrop}
    >
      {vm.questions.map((questionVm, index) => (
        <QuestionCardContainer
          key={questionVm.cid}
          vm={questionVm}
          testId={testId && `${testId}-card-${questionVm.id || index}`}
        />
      ))}
    </QuestionList>
  )
})

interface QuestionCardContainerProps {
  vm: QuestionEditorViewModelBase
  testId?: string
}

const QuestionCardContainer = observer(function QuestionCardContainer({
  vm,
  testId,
}: QuestionCardContainerProps) {
  const questionVm = asAnyQuestionEditor(vm)
  const pathVm = questionVm.type === 'Path' ? questionVm : null
  return (
    <QuestionCard
      type={vm.type}
      id={vm.id}
      title={vm.cardTitle}
      friendlyType={vm.info.friendlyType}
      error={firstError(vm.validation)}
      isDeprecated={vm.info.deprecated}
      conditionCount={vm.conditionCount}
      valueType={vm.valueType}
      isSelected={vm.isSelected}
      isCollapsible={!!vm.info.collapsible}
      isCollapsed={vm.isCollapsed}
      isDisabled={vm.isEditingDisabled}
      onPress={vm.select}
      onToggleCollapse={vm.toggleCollapsed}
      onFocusId={vm.focusIdField}
      onDuplicate={vm.info.deprecated ? undefined : vm.duplicate}
      onRemove={vm.remove}
      onSetupInventoryItems={pathVm?.addInventoryItems}
      onDragStart={vm.startDrag}
      onDragEnd={vm.parentQuestionsBuilder.endDrag}
      canAcceptDrop={vm.canAcceptDrop}
      onDrop={vm.acceptDrop}
      onMove={vm.moveBy}
      scrollIntoView$={vm.scrollIntoViewRequest$}
      preview={
        vm.info.isMetaElement ? undefined : (
          <QuestionPreviewContainer vm={questionVm} />
        )
      }
      pathQuestions={
        pathVm ? (
          <QuestionListContainer vm={pathVm.questions} testId={testId} />
        ) : undefined
      }
      testId={testId}
    />
  )
})
